import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpenIcon, ClockIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui/Button';
import { ListSkeleton } from '../../ui';
import type { HomeworkAssignment } from '../../../types/stepWork';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase/config';

interface PendingHomeworkWidgetProps {
  userId: string;
}

export function PendingHomeworkWidget({ userId }: PendingHomeworkWidgetProps) {
  const navigate = useNavigate();
  const [pendingHomework, setPendingHomework] = useState<HomeworkAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPendingHomework();
  }, [userId]);

  async function loadPendingHomework() {
    try {
      setLoading(true);

      // Get all step progress for this user
      const progressRef = collection(db, 'users', userId, 'stepProgress');
      const progressSnapshot = await getDocs(progressRef);

      const allHomework: HomeworkAssignment[] = [];

      // For each step progress, get pending homework
      for (const progressDoc of progressSnapshot.docs) {
        const homeworkRef = collection(
          db,
          'users',
          userId,
          'stepProgress',
          progressDoc.id,
          'homework'
        );
        const q = query(
          homeworkRef,
          where('status', 'in', ['assigned', 'in_progress']),
          orderBy('assignedAt', 'asc')
        );
        const homeworkSnapshot = await getDocs(q);

        homeworkSnapshot.docs.forEach(doc => {
          allHomework.push({
            id: doc.id,
            ...doc.data(),
          } as HomeworkAssignment);
        });
      }

      // Sort by due date (soonest first), then by assigned date
      const sorted = allHomework.sort((a, b) => {
        if (a.dueDate && b.dueDate) {
          return a.dueDate.toMillis() - b.dueDate.toMillis();
        }
        if (a.dueDate && !b.dueDate) return -1;
        if (!a.dueDate && b.dueDate) return 1;
        return a.assignedAt.toMillis() - b.assignedAt.toMillis();
      });

      // Take top 5
      setPendingHomework(sorted.slice(0, 5));
    } catch (error) {
      console.error('Error loading pending homework:', error);
    } finally {
      setLoading(false);
    }
  }

  function getDueDateInfo(dueDate: any) {
    if (!dueDate) return null;

    const now = new Date();
    const due = dueDate.toDate();
    const daysUntil = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    let color = 'text-success-600 dark:text-success-400';
    let text = '';

    if (daysUntil < 0) {
      color = 'text-danger-600 dark:text-danger-400';
      text = `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) > 1 ? 's' : ''}`;
    } else if (daysUntil === 0) {
      color = 'text-warning-600 dark:text-warning-400';
      text = 'Due today';
    } else if (daysUntil === 1) {
      color = 'text-warning-600 dark:text-warning-400';
      text = 'Due tomorrow';
    } else if (daysUntil <= 3) {
      color = 'text-warning-600 dark:text-warning-400';
      text = `Due in ${daysUntil} days`;
    } else {
      text = `Due ${due.toLocaleDateString()}`;
    }

    return { color, text };
  }

  function handleViewHomework(assignment: HomeworkAssignment) {
    // Navigate to the step detail page, homework tab
    navigate(`/steps/${assignment.fellowship}/${assignment.stepNumber}?tab=homework`);
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-surface-800 rounded-xl p-6 border border-surface-200 dark:border-surface-700">
        <h3 className="font-display text-lg font-bold text-navy-800 dark:text-gold-500 mb-4">
          Pending Homework
        </h3>
        <ListSkeleton count={3} />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-surface-800 rounded-xl p-6 border border-surface-200 dark:border-surface-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold text-navy-800 dark:text-gold-500">
          Pending Homework
        </h3>
        {pendingHomework.length > 0 && (
          <span className="text-xs px-2 py-1 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
            {pendingHomework.length} pending
          </span>
        )}
      </div>

      {pendingHomework.length === 0 ? (
        <div className="text-center py-8">
          <BookOpenIcon className="h-12 w-12 mx-auto text-slate-400 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No pending homework assignments
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
            Your sponsor can assign readings and writings in your step work
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingHomework.map(assignment => {
            const dueDateInfo = getDueDateInfo(assignment.dueDate);

            return (
              <button
                key={assignment.id}
                onClick={() => handleViewHomework(assignment)}
                className="w-full text-left p-3 rounded-lg border border-slate-200 dark:border-navy-700 hover:bg-slate-50 dark:hover:bg-navy-900 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpenIcon className="h-4 w-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                      <h4 className="font-semibold text-sm text-navy-900 dark:text-navy-100 truncate">
                        {assignment.title}
                      </h4>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
                      <span className="px-2 py-0.5 bg-slate-100 dark:bg-navy-800 rounded">
                        Step {assignment.stepNumber}
                      </span>
                      <span className="capitalize">{assignment.type}</span>
                      {assignment.status === 'in_progress' && (
                        <span className="text-teal-600 dark:text-teal-400 font-semibold">
                          In Progress
                        </span>
                      )}
                    </div>

                    {dueDateInfo && (
                      <div className={`flex items-center gap-1 text-xs mt-1 ${dueDateInfo.color}`}>
                        <ClockIcon className="h-3 w-3" />
                        <span>{dueDateInfo.text}</span>
                      </div>
                    )}
                  </div>

                  <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-slate-600 flex-shrink-0" />
                </div>
              </button>
            );
          })}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/steps')}
            className="w-full mt-2"
          >
            View All Step Work
          </Button>
        </div>
      )}
    </div>
  );
}
